import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, ArrowUpRightIcon, CheckIcon, ChevronDownIcon, GlobeIcon, MenuIcon, XIcon } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { KaykoLogo } from './KaykoLogo';
import { SmartLink } from './SmartLink';
import { NavMenuPanel } from './navigation/NavMenuPanel';
import { mainNav } from '../data/navigation';
const EASE = [0.23, 1, 0.32, 1] as const;
const LANGUAGES = [{
  code: 'EN',
  label: 'English'
}, {
  code: 'RW',
  label: 'Kinyarwanda'
}, {
  code: 'FR',
  label: 'Français'
}];
export function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [mobileSection, setMobileSection] = useState<string | null>(null);
  const [langOpen, setLangOpen] = useState(false);
  const [lang, setLang] = useState('EN');
  const closeTimer = useRef<number | null>(null);
  const langRef = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion();
  const {
    pathname
  } = useLocation();
  const overlay = pathname === '/';
  const [dark, setDark] = useState(overlay);

  // Switch the bar to its dark treatment only while it sits over a section
  // marked data-nav-theme="dark"; everywhere else (mostly white) it is light.
  useEffect(() => {
    let frame = 0;
    const probeY = 40;
    const update = () => {
      frame = 0;
      const sections = document.querySelectorAll<HTMLElement>('[data-nav-theme="dark"]');
      let over = false;
      sections.forEach(el => {
        const r = el.getBoundingClientRect();
        if (r.top <= probeY && r.bottom >= probeY) over = true;
      });
      setDark(over);
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    const t = window.setTimeout(update, 60);
    window.addEventListener('scroll', schedule, {
      passive: true
    });
    window.addEventListener('resize', schedule);
    return () => {
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      window.clearTimeout(t);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [pathname]);
  const navText = dark ? 'text-[15px] font-medium text-white/90 transition-colors duration-150 ease-out hover:text-white' : 'text-[15px] font-medium text-ink transition-colors duration-150 ease-out hover:text-ink-muted';
  const chevron = dark ? 'text-white/70' : 'text-ink-muted';
  useEffect(() => {
    setActiveMenu(null);
    setMobileOpen(false);
    setMobileSection(null);
  }, [pathname]);
  const active = mainNav.find(m => m.label === activeMenu && m.groups);
  const cancelClose = () => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = window.setTimeout(() => setActiveMenu(null), 140);
  };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setActiveMenu(null);
        setLangOpen(false);
      }
    };
    const onClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, []);
  return <>
      {/* Zero-height sticky header: the floating bar overlaps the page without reserving any space above it */}
      <header className="sticky top-0 z-50 h-0 w-full overflow-visible px-3 sm:px-5" data-id="element-834" data-mp="k2gih73fzd11">
        <div onMouseLeave={scheduleClose} onMouseEnter={cancelClose} className="relative top-3 mx-auto max-w-[1400px]" data-id="element-835" data-mp="o38ta4ryppzf">
          <div className={`relative z-20 rounded-[12px] border backdrop-blur-md transition-[background-color,border-color,box-shadow] duration-200 ease-out ${dark ? 'border-white/10 bg-[#141c55]/85 shadow-[0_12px_32px_-12px_rgba(8,12,50,0.55)]' : 'border-ink/10 bg-white/75 shadow-[0_12px_32px_-16px_rgba(11,11,11,0.22)]'}`} data-id="element-836" data-mp="1bsupqkgy9r3q">
            <nav aria-label="Main" className="flex h-[52px] items-center justify-between pl-5 pr-2" data-id="element-837" data-mp="3f5klgdee8oi2">
              <div className="flex items-center gap-8" data-id="element-838" data-mp="3b4iipml9pubc">
                <SmartLink href="/" aria-label="Kayko home" onMouseEnter={() => setActiveMenu(null)} data-id="element-839" data-mp="blpn34esrysz">
                  <KaykoLogo tone={dark ? 'light' : 'dark'} className="!h-[24px]" data-id="element-840" data-mp="6p8xe5ry9cds" />
                </SmartLink>

                <ul className="hidden items-center gap-1 lg:flex" data-id="element-841" data-mp="1vo76zwllmp5">
                  {mainNav.map(menu => {
                  const isOpen = activeMenu === menu.label;
                  if (!menu.groups) {
                    return <li key={menu.label} data-id="element-842" data-mp="pn1zj5ur1ia4">
                          <SmartLink href={menu.href} onMouseEnter={() => setActiveMenu(null)} aria-current={menu.href && pathname === menu.href ? 'page' : undefined} className={`block rounded-[7px] px-2.5 py-1.5 ${navText}`} data-id="element-843" data-mp="pl8x62r65k7x">
                            {menu.label}
                          </SmartLink>
                        </li>;
                  }
                  return <li key={menu.label} data-id="element-844" data-mp="2w792ehjcrr19">
                        <button type="button" aria-expanded={isOpen} aria-haspopup="true" onMouseEnter={() => {
                      cancelClose();
                      setActiveMenu(menu.label);
                    }} onClick={() => setActiveMenu(isOpen ? null : menu.label)} className={`flex items-center gap-1 rounded-[7px] px-2.5 py-1.5 ${navText} ${isOpen ? dark ? 'bg-white/10 text-white' : 'bg-ink/[0.06]' : ''}`} data-id="element-845" data-mp="2w5xwoc6ck97i">
                          {menu.label}
                          <ChevronDownIcon className={`h-4 w-4 ${chevron} transition-transform duration-200 ease-out ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" data-id="element-846" data-mp="1om2cc0ulgmck" />
                        </button>
                      </li>;
                })}
                </ul>
              </div>

              <div className="hidden items-center gap-5 lg:flex" onMouseEnter={() => setActiveMenu(null)} data-id="element-847" data-mp="27k671q9qiiuu">
                <div ref={langRef} className="relative" data-id="element-848" data-mp="22kto3glzhvxj">
                  <button type="button" aria-label={`Language: ${LANGUAGES.find(l => l.code === lang)?.label}`} aria-expanded={langOpen} aria-haspopup="listbox" onClick={() => setLangOpen(v => !v)} className={`flex items-center gap-1 rounded-[7px] px-1.5 py-1.5 ${navText}`} data-id="element-849" data-mp="22j8pnfwjtsue">
                    <GlobeIcon className="h-4 w-4" aria-hidden="true" data-id="element-850" data-mp="2a4uf1gr95rtv" />
                    <ChevronDownIcon className={`h-3.5 w-3.5 ${chevron}`} aria-hidden="true" data-id="element-851" data-mp="2a4krs0xldyrv" />
                  </button>
                  <AnimatePresence data-id="element-852" data-mp="21461en5rtthi">
                    {langOpen && <motion.ul role="listbox" aria-label="Choose language" initial={reduceMotion ? {
                    opacity: 0
                  } : {
                    opacity: 0,
                    y: -4,
                    scale: 0.97
                  }} animate={{
                    opacity: 1,
                    y: 0,
                    scale: 1
                  }} exit={reduceMotion ? {
                    opacity: 0
                  } : {
                    opacity: 0,
                    y: -4,
                    scale: 0.97
                  }} transition={{
                    duration: 0.15,
                    ease: EASE
                  }} className="absolute right-0 top-full mt-3 w-[180px] origin-top-right rounded-[10px] border border-canvas-line bg-white p-1 shadow-[0_16px_40px_-16px_rgba(11,11,11,0.35)]" data-id="element-853" data-mp="212uz6rrz4tsi">
                        {LANGUAGES.map(l => <li key={l.code} role="option" aria-selected={lang === l.code} data-id="element-854" data-mp="24jxw4u5033fu">
                            <button type="button" onClick={() => {
                        setLang(l.code);
                        setLangOpen(false);
                      }} className="flex w-full items-center justify-between rounded-[7px] px-3 py-2 text-left text-[14px] text-ink transition-colors duration-150 ease-out hover:bg-canvas-warm" data-id="element-855" data-mp="24hl5ztp39mrp">
                              {l.label}
                              {lang === l.code && <CheckIcon className="h-4 w-4 text-brand" aria-hidden="true" data-id="element-856" data-mp="3fy1pby3rknju" />}
                            </button>
                          </li>)}
                      </motion.ul>}
                  </AnimatePresence>
                </div>
                <SmartLink href="/request-demo" className="group inline-flex items-center gap-1.5 rounded-[8px] bg-brand px-4 py-2 text-[15px] font-semibold text-white transition-colors duration-150 ease-out hover:bg-brand-hover" data-id="element-857" data-mp="3nhkdsjvs18zt">
                  Get started
                  <ArrowUpRightIcon className="h-4 w-4 transition-transform duration-150 ease-out group-hover:-translate-y-px group-hover:translate-x-px" aria-hidden="true" data-id="element-858" data-mp="2zlye1f5qphet" />
                </SmartLink>
              </div>

              <button type="button" className={`flex h-10 w-10 items-center justify-center rounded-[8px] lg:hidden ${dark ? 'text-white' : 'text-ink'}`} aria-label={mobileOpen ? 'Close menu' : 'Open menu'} aria-expanded={mobileOpen} onClick={() => setMobileOpen(v => !v)} data-id="element-859" data-mp="3ei7bcb62lkol">
                {mobileOpen ? <XIcon className="h-6 w-6" data-id="element-860" data-mp="gy8d3mdfs2rq" /> : <MenuIcon className="h-6 w-6" data-id="element-861" data-mp="gxfex9sjmll7" />}
              </button>
            </nav>
          </div>

          <AnimatePresence data-id="element-862" data-mp="lq08qbbg8yoq">
            {active && <motion.div key="panel" initial={reduceMotion ? {
            opacity: 0
          } : {
            opacity: 0,
            y: -6
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={reduceMotion ? {
            opacity: 0
          } : {
            opacity: 0,
            y: -6
          }} transition={{
            duration: 0.2,
            ease: EASE
          }} className="absolute inset-x-0 top-full z-10 hidden pt-2 lg:block" data-id="element-863" data-mp="qoa16e5shl6n">
                <div className="overflow-hidden rounded-[14px] border border-canvas-line bg-white shadow-[0_30px_60px_-30px_rgba(11,11,11,0.35)]" data-id="element-864" data-mp="7ybuo2hn4054">
                  <AnimatePresence mode="wait" initial={false} data-id="element-865" data-mp="1eg8fz84g5yoj">
                    <motion.div key={active.label} initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} transition={{
                  duration: 0.12,
                  ease: 'easeOut'
                }} data-id="element-866" data-mp="1jbxklpvbq5rp">
                      <NavMenuPanel menu={active} data-id="element-867" data-mp="2u45adh5scfuj" />
                    </motion.div>
                  </AnimatePresence>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>

        <AnimatePresence data-id="element-868" data-mp="2l6316ebwegw6">
          {active && <motion.div key="backdrop" aria-hidden="true" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2,
          ease: 'easeOut'
        }} onClick={() => setActiveMenu(null)} className="fixed inset-0 -z-10 hidden bg-ink/40 backdrop-blur-[3px] lg:block" data-id="element-869" data-mp="2g5feu5ym6o8a" />}
        </AnimatePresence>

        {mobileOpen && <div className="mx-auto mt-2 max-h-[calc(100vh-96px)] max-w-[1400px] overflow-y-auto rounded-[14px] border border-canvas-line bg-white px-5 py-4 shadow-[0_24px_48px_-24px_rgba(11,11,11,0.35)] lg:hidden" data-id="element-870" data-mp="1ywmk90exajl7">
            <ul className="divide-y divide-canvas-line" data-id="element-871" data-mp="1oyydtd7yfnn7">
              {mainNav.map(menu => {
            const expanded = mobileSection === menu.label;
            if (!menu.groups) {
              return <li key={menu.label} data-id="element-872" data-mp="2ho5k4ydnhhj0">
                      <SmartLink href={menu.href} className="block py-3.5 text-[16px] font-medium text-ink" data-id="element-873" data-mp="2mlxd7vq179os">
                        {menu.label}
                      </SmartLink>
                    </li>;
            }
            return <li key={menu.label} data-id="element-874" data-mp="2cm6lg1suv8se">
                    <button type="button" aria-expanded={expanded} onClick={() => setMobileSection(expanded ? null : menu.label)} className="flex w-full items-center justify-between py-3.5 text-[16px] font-medium text-ink" data-id="element-875" data-mp="x791zxt6fxsc">
                      {menu.label}
                      <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ease-out ${expanded ? 'rotate-180 text-brand' : 'text-ink-muted'}`} data-id="element-876" data-mp="os320f1yiqcv" />
                    </button>
                    {expanded && <div className="space-y-5 pb-4" data-id="element-877" data-mp="5ziktuvme9ow">
                        {menu.groups.map(group => <div key={group.heading} data-id="element-878" data-mp="kowy3e2bhb9k">
                            <p className="border-b border-canvas-line pb-2 text-[13px] font-medium text-ink-muted" data-id="element-879" data-mp="kppylxaon4j9">
                              {group.heading}
                            </p>
                            <ul className="mt-2" data-id="element-880" data-mp="3obnj3f8b937s">
                              {group.items.map(item => {
                      const Icon = item.icon;
                      return <li key={item.label} data-id="element-881" data-mp="2mkxe0rix0z6b">
                                    <SmartLink href={item.href} className="flex items-center gap-3 py-2" data-id="element-882" data-mp="2qntyyovxh7df">
                                      <span className="flex h-9 w-9 items-center justify-center rounded-[8px] border border-canvas-line" data-id="element-883" data-mp="2vmnesnyotagr">
                                        <Icon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-884" data-mp="30ghxpp6gxles" />
                                      </span>
                                      <span className="text-[15px] text-ink" data-id="element-885" data-mp="3abtai1ybgijw">{item.label}</span>
                                    </SmartLink>
                                  </li>;
                    })}
                            </ul>
                          </div>)}
                        {menu.footerLink && <SmartLink href={menu.footerLink.href} className="inline-flex items-center gap-1.5 text-[15px] font-medium text-brand" data-id="element-886" data-mp="1oyq9fhmm36ty">
                            {menu.footerLink.label}
                            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-887" data-mp="17pxhyky7vib3" />
                          </SmartLink>}
                      </div>}
                  </li>;
          })}
            </ul>
            <div className="mt-4 flex items-center gap-2 border-t border-canvas-line pt-4" role="group" aria-label="Language" data-id="element-888" data-mp="20dtmucxxwgiy">
              <GlobeIcon className="h-4 w-4 text-ink-muted" aria-hidden="true" data-id="element-889" data-mp="1vdp0w7e9itlc" />
              {LANGUAGES.map(l => <button key={l.code} type="button" aria-pressed={lang === l.code} onClick={() => setLang(l.code)} className={`rounded-[6px] px-2.5 py-1 text-[14px] font-medium ${lang === l.code ? 'bg-ink text-white' : 'text-ink-muted'}`} data-id="element-890" data-mp="1mfd57qyjh5m1">
                  {l.code}
                </button>)}
            </div>
            <div className="mt-4 flex flex-col gap-3 border-t border-canvas-line pt-4" data-id="element-891" data-mp="hdknhuqk6f31">
              <SmartLink href="/request-demo" className="inline-flex items-center justify-center gap-1.5 rounded-[7px] bg-brand px-5 py-3 text-[16px] font-semibold text-white" data-id="element-892" data-mp="2kqvj9u3o6hh">
                Get started
                <ArrowUpRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-893" data-mp="3umoyrccfggvn" />
              </SmartLink>
            </div>
          </div>}
      </header>
    </>;
}